import { versionCompare } from '../Platform/Platform';
import { ValueError } from '../Utility/Error';

/**
 * Get the Player UUID of regular players.
 *
 * @returns Returns `null` if the UUID doesn't exist.
 * @see {@link getPreviewIds} for preview players.
 */
export function getPlayerUuid(): string | null {
    const parsedUrl = new URL(window.location.href);
    return parsedUrl.searchParams.get('id');
}

/** IDs used to identify Preview Players */
export interface PreviewIds {
    /** CMS Security Token */
    securityToken: string;
    /** The layout ID */
    displayId: string;
    /** Used to validate api call for previewing layouts against v2 permissions */
    permissionsV2: string | null;
}

/**
 * Get the IDs used by Preview Players.
 *
 * @returns Returns `null` if the IDs don't exist.
 * @see {@link getPlayerUuid} for regular players.
 */
export function getPreviewIds(): PreviewIds | null {
    const parsedUrl = new URL(window.location.href);
    const securityToken = parsedUrl.searchParams.get('token');
    const displayId = parsedUrl.searchParams.get('display_id');
    const permissionsV2 = parsedUrl.searchParams.get('permissionsV2');

    if (securityToken && displayId) {
        return {
            securityToken,
            displayId,
            permissionsV2,
        };
    }

    return null;
}

/**
 * Determine minimum Signage version requirement
 *
 * Returns True if the Signage version is greater than or equal to the version
 * number represented by the passed string (e.g. "4.0.13").
 *
 * _Note: It is better to use feature detection then this._
 *
 * @throws {@link ValueError} when the unable to determine the Signage version
 */
export function isAtLeastVersion(minVersion: string): boolean | never {
    const currentSignsVersion = getVersion();
    const hasValidVersion = currentSignsVersion !== 'unknown';

    if (!hasValidVersion) {
        throw new ValueError('Could not determine Signage version.');
    }

    if (versionCompare(currentSignsVersion, minVersion) >= 0) {
        return true;
    } else {
        return false;
    }
}

/**
 * Get Signage Version Number
 * @return Will return 'unknown' if version cannot be determined
 */
export function getVersion(): string {
    const runningVersionMatch = /\/v\/([0-9]+\.[0-9]+\.[0-9]+\.[0-9]+|[a-z]+)\//.exec(
        window.location.href
    );
    return runningVersionMatch ? runningVersionMatch[1] : 'unknown';
}
